import type { ComponentChild } from 'preact';
import cn from 'classnames';
import FeatureIcon, {
  type FeatureIconProps
} from '@nespresso/catalog-components-react/controls/FeatureIcon';
import IntensityMeter from '@nespresso/catalog-components-react/controls/IntensityMeter';
import { MAXIMUM_COFFEE_INTENSITY, type Product } from '@/domain/Product';
import { type PropertySpecification } from '@/domain/Specification';
import {
  productCupSizeSpecToFeatureIcons,
  getRecipeIconDetails
} from '@/widgets/utils/FeatureIconsLookup.ts';
import styles from './CoffeeSpecification.module.scss';
import useWidgetOptions from '@/widgets/hooks/useWidgetOptions.tsx';
import type { Options } from '@/widgets/ProductList/config';

type CoffeeSpecificationProps = {
  product: Product;
  class?: string;
};
export default function CoffeeSpecification({
  product,
  class: className = ''
}: CoffeeSpecificationProps) {
  const {
    hideIntensityMeterCard,
    hideFeatureIcons,
    cupSizeDisplayFormat,
    cupSizeVolumeUnits,
    cupSizeVolumesDecimalRounding
  } = useWidgetOptions<Options>();
  if (hideIntensityMeterCard && hideFeatureIcons) {
    return <FeaturesContainer class={className} />;
  }

  const intensity = product.specifications.intensity as PropertySpecification;
  const intensityLabel = intensity?.title;
  const intensityValue = Number(intensity?.value);

  // map our specs to generic feature icons
  const cupSizes = product.specifications.cupSizes as PropertySpecification[];
  const recipes = product.specifications.recipes as PropertySpecification[];

  const featureIcons: FeatureIconProps['icons'] =
    productCupSizeSpecToFeatureIcons(cupSizes ?? [], {
      preferredUnits: cupSizeVolumeUnits,
      showIcon: cupSizeDisplayFormat.includes('icon'),
      showCupSizeName: cupSizeDisplayFormat.includes('label'),
      showLiquidVolume: cupSizeDisplayFormat.includes('volume'),
      decimalRounding: cupSizeVolumesDecimalRounding
    });

  const recipeIcons: FeatureIconProps['icons'] = recipes
    ? getRecipeIconDetails(recipes)
    : [];

  return (
    <FeaturesContainer class={className}>
      {!hideIntensityMeterCard && intensityValue ? (
        <IntensityMeter
          intensity={intensityValue}
          maximum={MAXIMUM_COFFEE_INTENSITY}
          label={intensityLabel}
        />
      ) : null}
      {!hideFeatureIcons && (
        <FeatureIcon
          icons={[...featureIcons, ...recipeIcons]}
          className={styles.featureIcons}
          separator={<div class={styles.separator}>&middot;</div>}
        />
      )}
    </FeaturesContainer>
  );
}

type FeaturesContainerProps = {
  children?: ComponentChild;
  class?: string;
};
function FeaturesContainer({
  children,
  class: className = ''
}: FeaturesContainerProps) {
  return <div class={cn(styles.features, className)}>{children}</div>;
}
